/**
 * Client Chess.com minimal (API publique en lecture seule, aucune clé,
 * aucun mot de passe). Appelé directement depuis le navigateur — l'API
 * expose `access-control-allow-origin: *`. Isolé du reste de l'app :
 * ne connaît ni le stockage, ni l'UI.
 */

export interface ChessComGame {
  pgn: string;
  url: string;
  endTime: number; // epoch secondes
  timeClass: string; // "bullet" | "blitz" | "rapid" | "daily" | ...
  rated: boolean;
  white: { username: string; rating: number; result: string };
  black: { username: string; rating: number; result: string };
}

async function fetchSafe(url: string): Promise<Response> {
  try {
    return await fetch(url);
  } catch {
    throw new Error('Impossible de joindre Chess.com (connexion indisponible ?).');
  }
}

/** Récupère les parties récentes (échecs standard uniquement) d'un pseudo Chess.com. */
export async function fetchChessComGames(
  username: string,
  opts: { months?: number; limit?: number } = {},
): Promise<ChessComGame[]> {
  const name = username.trim().toLowerCase();
  if (!name) throw new Error("Nom d'utilisateur requis.");

  const profileRes = await fetchSafe(`https://api.chess.com/pub/player/${encodeURIComponent(name)}`);
  if (profileRes.status === 404) {
    throw new Error(`Joueur « ${name} » introuvable sur Chess.com.`);
  }
  if (!profileRes.ok) {
    throw new Error('Chess.com injoignable, réessaie plus tard.');
  }

  const archivesRes = await fetchSafe(`https://api.chess.com/pub/player/${encodeURIComponent(name)}/games/archives`);
  if (!archivesRes.ok) {
    throw new Error('Chess.com injoignable, réessaie plus tard.');
  }
  const archivesData = (await archivesRes.json()) as { archives?: string[] };
  const archives = archivesData.archives ?? [];
  const months = opts.months ?? 3;
  const recentArchives = archives.slice(-months).reverse();

  const games: ChessComGame[] = [];
  for (const url of recentArchives) {
    const res = await fetchSafe(url);
    if (!res.ok) continue; // un mois indisponible ne doit pas faire échouer les autres
    const data = (await res.json()) as { games?: Record<string, unknown>[] };
    for (const g of data.games ?? []) {
      if (typeof g.rules === 'string' && g.rules !== 'chess') continue; // échecs standard uniquement
      if (typeof g.pgn !== 'string' || !g.pgn) continue;
      const white = (g.white ?? {}) as Record<string, unknown>;
      const black = (g.black ?? {}) as Record<string, unknown>;
      games.push({
        pgn: g.pgn,
        url: typeof g.url === 'string' ? g.url : '',
        endTime: typeof g.end_time === 'number' ? g.end_time : 0,
        timeClass: typeof g.time_class === 'string' ? g.time_class : '',
        rated: Boolean(g.rated),
        white: {
          username: typeof white.username === 'string' ? white.username : '',
          rating: typeof white.rating === 'number' ? white.rating : 0,
          result: typeof white.result === 'string' ? white.result : '',
        },
        black: {
          username: typeof black.username === 'string' ? black.username : '',
          rating: typeof black.rating === 'number' ? black.rating : 0,
          result: typeof black.result === 'string' ? black.result : '',
        },
      });
    }
  }

  games.sort((a, b) => b.endTime - a.endTime);
  return games.slice(0, opts.limit ?? 60);
}

const DRAW_RESULTS = new Set(['agreed', 'repetition', 'stalemate', 'insufficient', '50move', 'timevsinsufficient']);

/** Classe un résultat de partie Chess.com du point de vue du joueur étudié. */
export function outcomeFor(result: string): 'win' | 'draw' | 'loss' {
  if (result === 'win') return 'win';
  if (DRAW_RESULTS.has(result)) return 'draw';
  return 'loss';
}

export const TIME_CLASS_LABELS: Record<string, string> = {
  bullet: 'Bullet',
  blitz: 'Blitz',
  rapid: 'Rapide',
  daily: 'Correspondance',
};
